import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { HelpArticle, HelpArticleFormData } from '../types/helpArticle';

const COLLECTION_NAME = 'help_articles';

class HelpArticleService {
  // Create a new help article
  async createArticle(data: HelpArticleFormData, userId: string): Promise<string> {
    try {
      const articleRef = collection(db, COLLECTION_NAME);
      const newArticle = {
        ...data,
        views: 0,
        helpfulCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      };

      const docRef = await addDoc(articleRef, newArticle);
      return docRef.id;
    } catch (error) {
      console.error('Error creating help article:', error);
      throw new Error('Failed to create help article');
    }
  }

  // Get all articles
  async getAllArticles(includeInactive = false): Promise<HelpArticle[]> {
    try {
      const articlesRef = collection(db, COLLECTION_NAME);
      let q = query(articlesRef, orderBy('order', 'asc'), orderBy('createdAt', 'desc'));

      if (!includeInactive) {
        q = query(articlesRef, where('isActive', '==', true), orderBy('order', 'asc'), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || Timestamp.now(),
        updatedAt: doc.data().updatedAt || Timestamp.now(),
      })) as HelpArticle[];
    } catch (error) {
      console.error('Error fetching help articles:', error);
      throw new Error('Failed to fetch help articles');
    }
  }

  // Get articles by category
  async getArticlesByCategory(category: string, includeInactive = false): Promise<HelpArticle[]> {
    try {
      const articlesRef = collection(db, COLLECTION_NAME);
      let q = query(
        articlesRef,
        where('category', '==', category),
        orderBy('order', 'asc'),
        orderBy('createdAt', 'desc')
      );

      if (!includeInactive) {
        q = query(
          articlesRef,
          where('category', '==', category),
          where('isActive', '==', true),
          orderBy('order', 'asc'),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || Timestamp.now(),
        updatedAt: doc.data().updatedAt || Timestamp.now(),
      })) as HelpArticle[];
    } catch (error) {
      console.error('Error fetching articles by category:', error);
      throw new Error('Failed to fetch articles by category');
    }
  }

  // Get featured articles
  async getFeaturedArticles(): Promise<HelpArticle[]> {
    try {
      const articlesRef = collection(db, COLLECTION_NAME);
      const q = query(
        articlesRef,
        where('isFeatured', '==', true),
        where('isActive', '==', true),
        orderBy('order', 'asc'),
        limit(10)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || Timestamp.now(),
        updatedAt: doc.data().updatedAt || Timestamp.now(),
      })) as HelpArticle[];
    } catch (error) {
      console.error('Error fetching featured articles:', error);
      throw new Error('Failed to fetch featured articles');
    }
  }

  // Search articles
  async searchArticles(searchTerm: string): Promise<HelpArticle[]> {
    try {
      const allArticles = await this.getAllArticles(true);
      const term = searchTerm.toLowerCase();

      return allArticles.filter(
        (article) =>
          article.isActive &&
          (article.title.toLowerCase().includes(term) ||
            article.content.toLowerCase().includes(term) ||
            article.tags.some((tag) => tag.toLowerCase().includes(term)))
      );
    } catch (error) {
      console.error('Error searching articles:', error);
      throw new Error('Failed to search articles');
    }
  }

  // Get article by ID
  async getArticleById(id: string): Promise<HelpArticle | null> {
    try {
      const articleRef = doc(db, COLLECTION_NAME, id);
      const snapshot = await getDoc(articleRef);

      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: snapshot.id,
        ...snapshot.data(),
        createdAt: snapshot.data().createdAt || Timestamp.now(),
        updatedAt: snapshot.data().updatedAt || Timestamp.now(),
      } as HelpArticle;
    } catch (error) {
      console.error('Error fetching article:', error);
      throw new Error('Failed to fetch article');
    }
  }

  // Get article by slug
  async getArticleBySlug(slug: string): Promise<HelpArticle | null> {
    try {
      const articlesRef = collection(db, COLLECTION_NAME);
      const q = query(articlesRef, where('slug', '==', slug), where('isActive', '==', true), limit(1));

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || Timestamp.now(),
        updatedAt: doc.data().updatedAt || Timestamp.now(),
      } as HelpArticle;
    } catch (error) {
      console.error('Error fetching article by slug:', error);
      throw new Error('Failed to fetch article by slug');
    }
  }

  // Update article
  async updateArticle(id: string, data: Partial<HelpArticleFormData>, userId: string): Promise<void> {
    try {
      const articleRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(articleRef, {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });
    } catch (error) {
      console.error('Error updating article:', error);
      throw new Error('Failed to update article');
    }
  }

  // Delete article
  async deleteArticle(id: string): Promise<void> {
    try {
      const articleRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(articleRef);
    } catch (error) {
      console.error('Error deleting article:', error);
      throw new Error('Failed to delete article');
    }
  }

  // Increment view count
  async incrementViews(id: string): Promise<void> {
    try {
      const articleRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(articleRef, {
        views: increment(1),
      });
    } catch (error) {
      console.error('Error incrementing views:', error);
      // Don't throw - this is not critical
    }
  }

  // Increment helpful count
  async incrementHelpful(id: string): Promise<void> {
    try {
      const articleRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(articleRef, {
        helpfulCount: increment(1),
      });
    } catch (error) {
      console.error('Error incrementing helpful count:', error);
      // Don't throw - this is not critical
    }
  }

  // Generate slug from title
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }
}

export const helpArticleService = new HelpArticleService();

